import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Phone, Calendar, MapPin, Users } from 'react-feather';
import AuthService from '../../services/authService';
import PropertyDetailsModal from '../common/PropertyDetailsModal';

// `assignedPool` arrives populated from /users/with-assignments, but other
// callers hand this modal a user whose assignedPool is still a raw ObjectId.
// Normalize both shapes to an id string.
const poolIdOf = (p) => (p && typeof p === 'object' ? p._id : p) || '';

// Type-to-filter pool picker. Same value/onChange contract as the <select> it
// replaces. Matches on name, city AND state — state is searchable on purpose,
// since same-name cities in different states are the whole reason this list is
// long (Dayton OH vs Dayton TX).
const PoolSearchSelect = ({ pools, value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const selectedPool = pools.find(p => p._id === value) || null;

  const poolLabel = (p) => {
    const area = [p.serviceArea?.city, p.serviceArea?.state].filter(Boolean).join(', ');
    return area ? `${p.name} — ${area}` : p.name;
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? pools.filter(p => {
        const haystack = `${p.name || ''} ${p.serviceArea?.city || ''} ${p.serviceArea?.state || ''}`.toLowerCase();
        return q.split(/\s+/).every(token => haystack.includes(token));
      })
    : pools;

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const el = listRef.current.children[highlightIndex];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, isOpen]);

  useEffect(() => { setHighlightIndex(0); }, [query]);

  const selectPool = (pool) => {
    onChange(pool._id);
    setIsOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlightIndex]) selectPool(filtered[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        disabled={disabled}
        value={isOpen ? query : (selectedPool ? poolLabel(selectedPool) : '')}
        placeholder={selectedPool && !isOpen ? poolLabel(selectedPool) : 'Search pools by city, state, or name...'}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        onChange={(e) => { setQuery(e.target.value); if (!isOpen) setIsOpen(true); }}
        onKeyDown={handleKeyDown}
        style={{
          ...styles.select,
          boxSizing: 'border-box',
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
        }}
      />

      {selectedPool && !isOpen && (
        <button
          type="button"
          onClick={() => onChange('')}
          disabled={disabled}
          title="Clear selection"
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9ca3af',
            padding: '2px',
            lineHeight: 0,
          }}
        >
          <X size={14} />
        </button>
      )}

      {isOpen && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            // Sits inside a modal at zIndex 10000 — must clear its own content.
            zIndex: 10001,
            maxHeight: '240px',
            overflowY: 'auto',
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '9px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '13px', color: '#9ca3af' }}>
              No pools match "{query}"
            </div>
          ) : (
            filtered.map((p, idx) => (
              <div
                key={p._id}
                onMouseDown={(e) => { e.preventDefault(); selectPool(p); }}
                onMouseEnter={() => setHighlightIndex(idx)}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  backgroundColor: idx === highlightIndex ? '#f3f4f6' : (p._id === value ? '#eef2ff' : '#fff'),
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px',
                }}
              >
                <span style={{ fontWeight: 600, color: '#111827' }}>
                  {p.name}
                  {p.isActive === false && (
                    <span style={{ color: '#f59e0b', fontWeight: 500 }}> · inactive</span>
                  )}
                </span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {[p.serviceArea?.city, p.serviceArea?.state].filter(Boolean).join(', ') || 'No service area set'}
                  {` · ${p.agents?.length || 0} agents`}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const AdminUserModal = ({ user, isOpen, onClose, onUserUpdated, onPoolChanged }) => {
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState('');
  // Mirrors the user's CURRENT pool. Kept locally so assign/unassign updates the
  // display immediately — the parent passes a stale `user` prop until it refetches.
  const [assignedPoolId, setAssignedPoolId] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);

  // Admin-fetched activity (backend endpoints: /users/:userId/likes, /users/:userId/dislikes)
  const [fetchedLikes, setFetchedLikes] = useState([]);
  const [fetchedDislikes, setFetchedDislikes] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Admin-fetched Lauft bot transcript (read-only) — backend: GET /messages/lauft-bot/:userId
  const [botMessages, setBotMessages] = useState([]);
  const [botLoading, setBotLoading] = useState(false);
  const [showBotTranscript, setShowBotTranscript] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://back-end-x7tx.onrender.com';

  useEffect(() => {
    if (isOpen && user) {
      fetchPools();
      const pid = poolIdOf(user.assignedPool);
      setAssignedPoolId(pid);
      setSelectedPool(pid);
      fetchActivity(user._id);
    } else {
      setAssignedPoolId('');
      setSelectedPool('');
      // Reset activity when closed so it re-fetches on next open (and a stale user doesn't bleed through)
      setFetchedLikes([]);
      setFetchedDislikes([]);
      setBotMessages([]);
      setShowBotTranscript(false);
    }
  }, [isOpen, user]);

  // Fetch a user's liked + disliked properties (admin endpoints)
  const fetchActivity = async (userId) => {
    if (!userId) return;
    setActivityLoading(true);
    try {
      const headers = { Authorization: `Bearer ${AuthService.getToken()}` };
      const [likesRes, dislikesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/users/${userId}/likes`, { headers }),
        fetch(`${API_BASE_URL}/api/users/${userId}/dislikes`, { headers }),
      ]);
      const [likesJson, dislikesJson] = await Promise.all([
        likesRes.ok ? likesRes.json() : { data: [] },
        dislikesRes.ok ? dislikesRes.json() : { data: [] },
      ]);
      setFetchedLikes(likesJson.data || []);
      setFetchedDislikes(dislikesJson.data || []);
    } catch (err) {
      console.error('Error fetching user activity:', err);
      setFetchedLikes([]);
      setFetchedDislikes([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Fetch a user's Lauft bot conversation transcript (read-only, admin endpoint)
  const fetchBotConversation = async (userId) => {
    if (!userId) return;
    setBotLoading(true);
    setShowBotTranscript(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/messages/lauft-bot/${userId}`, {
        headers: { Authorization: `Bearer ${AuthService.getToken()}` },
      });
      const json = res.ok ? await res.json() : { data: { messages: [] } };
      setBotMessages(json.data?.messages || []);
    } catch (err) {
      console.error('Error fetching bot conversation:', err);
      setBotMessages([]);
    } finally {
      setBotLoading(false);
    }
  };

  const fetchPools = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/agent-pools`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Sorted by state then city — with a long list, a stable geographic
        // order makes the unfiltered scroll usable and groups same-name cities
        // apart (Dayton, Ohio nowhere near Dayton, Texas).
        const sorted = (data.data || []).slice().sort((a, b) => {
          const aKey = `${a.serviceArea?.state || 'zzz'} ${a.serviceArea?.city || a.name || ''}`;
          const bKey = `${b.serviceArea?.state || 'zzz'} ${b.serviceArea?.city || b.name || ''}`;
          return aKey.localeCompare(bKey);
        });
        setPools(sorted);
      }
    } catch (err) {
      console.error('Error fetching pools:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignPool = async () => {
    if (!selectedPool) {
      setError('Please select a pool');
      return;
    }

    setAssigning(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/assign-user-to-pool`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user._id,
          poolId: selectedPool
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('User assigned to pool successfully!');
        setAssignedPoolId(selectedPool);
        // Hand the parent the full pool doc so the table row and this modal's
        // `user` prop stay in sync without waiting on a refetch.
        if (onPoolChanged) {
          onPoolChanged(user._id, pools.find(p => p._id === selectedPool) || null);
        }
        if (onUserUpdated) {
          onUserUpdated();
        }
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        setError(data.message || 'Failed to assign user to pool');
      }
    } catch (err) {
      console.error('Error assigning pool:', err);
      setError('Failed to assign user to pool');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignPool = async () => {
    if (!window.confirm('Remove this user from their current pool?')) return;

    setAssigning(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/unassign-user-from-pool`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user._id
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('User removed from pool successfully!');
        setSelectedPool('');
        setAssignedPoolId('');
        if (onPoolChanged) {
          onPoolChanged(user._id, null);
        }
        if (onUserUpdated) {
          onUserUpdated();
        }
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        setError(data.message || 'Failed to remove user from pool');
      }
    } catch (err) {
      console.error('Error unassigning pool:', err);
      setError('Failed to remove user from pool');
    } finally {
      setAssigning(false);
    }
  };

  // Smooth-scroll to the Liked/Disliked section when a stat number is clicked
  const scrollToSection = (sectionId) => {
    const el = document.querySelector(`[data-section="${sectionId}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!isOpen || !user) return null;

  // Prefer the full doc from /api/agent-pools (always complete, and reflects
  // renames); fall back to whatever the user payload carried while it loads.
  const assignedPool =
    pools.find(p => p._id === assignedPoolId) ||
    (typeof user.assignedPool === 'object' ? user.assignedPool : null);

  const tierEmoji = {
    flex: '⚪',
    bronze: '🥉',
    silver: '🥈',
    gold: '🥇',
    platinum: '💎'
  };

  // LauftQ 16-type persona names, keyed by 4-axis type code (Dynamic/Calm, Modern/Traditional, Social/Private, Natural/Engineered)
  const LAUFTQ_NAMES = {
    DMSN: 'The Trailblazer', DMSE: 'The Maverick', DMPN: 'The Visionary', DMPE: 'The Architect',
    DTSN: 'The Connector', DTSE: 'The Host', DTPN: 'The Naturalist', DTPE: 'The Curator',
    CMSN: 'The Harmonizer', CMSE: 'The Designer', CMPN: 'The Minimalist', CMPE: 'The Strategist',
    CTSN: 'The Nurturer', CTSE: 'The Traditionalist', CTPN: 'The Homebody', CTPE: 'The Perfectionist',
  };

  // Normalize reported income to a true monthly figure. Valid frequencies are
  // 'weekly', 'biweekly', 'monthly' (no annual). monthlyIncome holds the raw entered number.
  const getMonthlyIncome = () => {
    const raw = user.onboardingData?.monthlyIncome;
    if (!raw) return null;
    const freq = user.onboardingData?.incomeFrequency;
    switch (freq) {
      case 'weekly': return Math.round(raw * 4.33);   // avg weeks per month
      case 'biweekly': return Math.round(raw * 2.17); // 26 pay periods / 12 months
      case 'monthly': return Math.round(raw);
      default: return Math.round(raw); // unknown/missing frequency — show as-is
    }
  };

  const lauftqCode = user.lauftqType;
  const lauftqName = lauftqCode ? (LAUFTQ_NAMES[lauftqCode] || null) : null;
  const monthlyIncome = getMonthlyIncome();

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{user.name}</h2>
            <p style={styles.subtitle}>User Details & Management</p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={24} />
          </button>
        </div>

        {/* Alert Messages */}
        {error && (
          <div style={styles.errorAlert}>
            {error}
          </div>
        )}
        {success && (
          <div style={styles.successAlert}>
            {success}
          </div>
        )}

        {/* Content */}
        <div style={styles.content}>
          {/* Pool Assignment Section */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Users size={20} style={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Pool Assignment</h3>
            </div>
            
            <div style={styles.poolAssignment}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Assign to Pool</label>
                <PoolSearchSelect
                  pools={pools}
                  value={selectedPool}
                  onChange={setSelectedPool}
                  disabled={assigning || loading}
                />
              </div>

              <div style={styles.poolActions}>
                <button
                  onClick={handleAssignPool}
                  disabled={assigning || !selectedPool || selectedPool === assignedPoolId}
                  style={{
                    ...styles.assignButton,
                    ...(assigning || !selectedPool || selectedPool === assignedPoolId ? styles.buttonDisabled : {})
                  }}
                >
                  {assigning ? 'Assigning...' : assignedPoolId ? 'Move to Pool' : 'Assign to Pool'}
                </button>

                {assignedPoolId && (
                  <button
                    onClick={handleUnassignPool}
                    disabled={assigning}
                    style={{
                      ...styles.unassignButton,
                      ...(assigning ? styles.buttonDisabled : {})
                    }}
                  >
                    Remove from Pool
                  </button>
                )}
              </div>

              {/* Always rendered — "None" is real information for an admin
                  deciding whether this user is in distribution at all. */}
              <div style={styles.currentPool}>
                <span style={styles.currentPoolLabel}>Current Pool:</span>
                <span style={styles.currentPoolValue}>
                  {!assignedPoolId ? (
                    <span style={{ opacity: 0.55 }}>None — not in any pool</span>
                  ) : assignedPool ? (
                    <>
                      {assignedPool.name || 'Unnamed pool'}
                      {assignedPool.serviceArea?.city &&
                        ` · ${assignedPool.serviceArea.city}${assignedPool.serviceArea.state ? `, ${assignedPool.serviceArea.state}` : ''}`}
                      {` · ${assignedPool.agents?.length || 0} agents`}
                      {assignedPool.isActive === false && (
                        <span style={{ color: '#f59e0b' }}> · INACTIVE</span>
                      )}
                    </>
                  ) : loading ? (
                    <span style={{ opacity: 0.55 }}>Loading…</span>
                  ) : (
                    // Not a thin payload — the user points at a pool that no
                    // longer exists (or one the list endpoint filters out).
                    <span style={{ color: '#f59e0b' }}>Unknown pool ({assignedPoolId})</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <Mail size={20} style={styles.sectionIcon} />
              <h3 style={styles.sectionTitle}>Contact Information</h3>
            </div>
            <div style={styles.infoGrid}>
              <div style={styles.infoItem}>
                <Mail size={16} style={styles.infoIcon} />
                <div>
                  <p style={styles.infoLabel}>Email</p>
                  <p style={styles.infoValue}>{user.email}</p>
                </div>
              </div>
              {user.phone && (
                <div style={styles.infoItem}>
                  <Phone size={16} style={styles.infoIcon} />
                  <div>
                    <p style={styles.infoLabel}>Phone</p>
                    <p style={styles.infoValue}>{user.phone}</p>
                  </div>
                </div>
              )}
              <div style={styles.infoItem}>
                <MapPin size={16} style={styles.infoIcon} />
                <div>
                  <p style={styles.infoLabel}>Location</p>
                  <p style={styles.infoValue}>{user.location || 'Not provided'}</p>
                </div>
              </div>
              <div style={styles.infoItem}>
                <Calendar size={16} style={styles.infoIcon} />
                <div>
                  <p style={styles.infoLabel}>Registered</p>
                  <p style={styles.infoValue}>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div style={styles.infoItem}>
                <Calendar size={16} style={styles.infoIcon} />
                <div>
                  <p style={styles.infoLabel}>Last Active</p>
                  <p style={styles.infoValue}>
                    {user.lastSwipedAt
                      ? new Date(user.lastSwipedAt).toLocaleDateString()
                      : 'No activity yet'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* User Stats */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Activity Stats</h3>
            <div style={styles.statsContainer}>
              <div
                style={styles.statBoxClickable}
                onClick={() => scrollToSection('liked-properties')}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37, 99, 235, 0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                title="Jump to liked properties"
              >
                <div style={styles.statNumber}>
                  {fetchedLikes.length || user.likesCount || user.likes?.length || 0}
                </div>
                <div style={styles.statLabel}>
                  LIKED
                </div>
              </div>

              <div style={styles.statDivider} />

              <div
                style={styles.statBoxClickable}
                onClick={() => scrollToSection('disliked-properties')}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37, 99, 235, 0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                title="Jump to passed properties"
              >
                <div style={styles.statNumber}>
                  {fetchedDislikes.length || user.dislikesCount || user.dislikes?.length || 0}
                </div>
                <div style={styles.statLabel}>
                  PASSED
                </div>
              </div>

              <div style={styles.statDivider} />

              <div style={styles.statBox}>
                <div style={styles.statNumber}>
                  {user.favorites?.length || 0}
                </div>
                <div style={styles.statLabel}>
                  FAVORITES
                </div>
              </div>
            </div>
          </div>

          {/* Lead Info */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Lead Information</h3>
            <div style={styles.leadInfo}>
              <div style={styles.leadBadge}>
                <span style={styles.leadTierEmoji}>{tierEmoji[user.leadTier?.toLowerCase()] || '❓'}</span>
                <span style={styles.leadTierText}>
                  {user.leadTier?.toUpperCase() || 'FLEX'} TIER
                </span>
              </div>
              <div style={styles.leadStatusBadge}>
                Status: {user.leadStatus || 'available'}
              </div>
              {lauftqCode && (
                <div style={styles.lauftqBadge}>
                  <span style={styles.lauftqCode}>{lauftqCode}</span>
                  {lauftqName && <span style={styles.lauftqName}>{lauftqName}</span>}
                </div>
              )}
              {user.assignedAgent && (
                <div style={styles.assignedAgentInfo}>
                  Assigned to: {user.assignedAgent.name || 'Unknown Agent'}
                </div>
              )}
            </div>
          </div>

          {/* Onboarding Data */}
          {user.onboardingData && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Onboarding Details</h3>
              <div style={styles.onboardingGrid}>
                {user.onboardingData.userGoal && (
                  <div style={styles.onboardingItem}>
                    <span style={styles.onboardingLabel}>User Goal:</span>
                    <span style={styles.onboardingValue}>
                      {user.onboardingData.userGoal === 'find_next' && 'Find Next Home'}
                      {user.onboardingData.userGoal === 'first_home' && 'First Home'}
                      {user.onboardingData.userGoal === 'rent' && 'Rent'}
                      {user.onboardingData.userGoal === 'lauftq' && 'Take LauftQ'}
                      {user.onboardingData.userGoal === 'browse' && 'Browse'}
                      {!['find_next', 'first_home', 'rent', 'lauftq', 'browse'].includes(user.onboardingData.userGoal) && user.onboardingData.userGoal}
                    </span>
                  </div>
                )}
                {user.onboardingData.livingSituation && (
                  <div style={styles.onboardingItem}>
                    <span style={styles.onboardingLabel}>Living Situation:</span>
                    <span style={styles.onboardingValue}>
                      {user.onboardingData.livingSituation === 'own' && 'Owns Home'}
                      {user.onboardingData.livingSituation === 'rent' && 'Renting'}
                      {user.onboardingData.livingSituation === 'family' && 'With Family'}
                      {user.onboardingData.livingSituation === 'other' && 'Other'}
                    </span>
                  </div>
                )}
                {monthlyIncome != null && (
                  <div style={styles.onboardingItem}>
                    <span style={styles.onboardingLabel}>Monthly Income:</span>
                    <span style={styles.onboardingValue}>
                      ${monthlyIncome.toLocaleString()}/mo
                      {user.onboardingData.incomeFrequency
                        && user.onboardingData.incomeFrequency !== 'monthly'
                        && ` (from ${user.onboardingData.monthlyIncome.toLocaleString()} ${user.onboardingData.incomeFrequency})`}
                    </span>
                  </div>
                )}
                {user.onboardingData.creditScore && (
                  <div style={styles.onboardingItem}>
                    <span style={styles.onboardingLabel}>Credit Score:</span>
                    <span style={styles.onboardingValue}>{user.onboardingData.creditScore}</span>
                  </div>
                )}
                {user.onboardingData.budgetExpense && (
                  <div style={styles.onboardingItem}>
                    <span style={styles.onboardingLabel}>Budget:</span>
                    <span style={styles.onboardingValue}>${user.onboardingData.budgetExpense.toLocaleString()}</span>
                  </div>
                )}
                {user.onboardingData.housingChoice && (
                  <div style={styles.onboardingItem}>
                    <span style={styles.onboardingLabel}>Looking For:</span>
                    <span style={styles.onboardingValue}>
                      {user.onboardingData.housingChoice === 'rent' ? 'Rent' : 'Buy'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Liked Properties */}
          {fetchedLikes.length > 0 && (
            <div style={styles.section} data-section="liked-properties">
              <h3 style={styles.sectionTitle}>Liked Properties ({fetchedLikes.length})</h3>
              <div style={styles.propertyGrid}>
                {fetchedLikes.slice(0, 6).map((property, index) => (
                  <div 
                    key={property._id || index} 
                    style={styles.propertyCard}
                    onClick={() => {
                      setSelectedProperty(property);
                      setShowPropertyModal(true);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {property.mainImage && (
                      <img
                        src={property.mainImage}
                        alt={property.address}
                        style={styles.propertyImage}
                      />
                    )}
                    <div style={styles.propertyInfo}>
                      <div style={styles.propertyAddress}>{property.address}</div>
                      {property.csz && (
                        <div style={styles.propertyCsz}>{property.csz}</div>
                      )}
                      <div style={styles.propertyPrice}>{property.price}</div>
                      <div style={styles.propertyDetails}>
                        {property.bedrooms} bed • {property.bathrooms} bath
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {fetchedLikes.length > 6 && (
                <div style={styles.moreProperties}>
                  + {fetchedLikes.length - 6} more properties
                </div>
              )}
            </div>
          )}

          {/* Disliked Properties */}
          {fetchedDislikes.length > 0 && (
            <div style={styles.section} data-section="disliked-properties">
              <h3 style={styles.sectionTitle}>Disliked Properties ({fetchedDislikes.length})</h3>
              <div style={styles.propertyGrid}>
                {fetchedDislikes.slice(0, 6).map((property, index) => (
                  <div 
                    key={property._id || index} 
                    style={styles.propertyCard}
                    onClick={() => {
                      setSelectedProperty(property);
                      setShowPropertyModal(true);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.borderColor = '#2563eb';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = '#f0f0f0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {property.mainImage && (
                      <img
                        src={property.mainImage}
                        alt={property.address}
                        style={styles.propertyImage}
                      />
                    )}
                    <div style={styles.propertyInfo}>
                      <div style={styles.propertyAddress}>{property.address}</div>
                      {property.csz && (
                        <div style={styles.propertyCsz}>{property.csz}</div>
                      )}
                      <div style={styles.propertyPrice}>{property.price}</div>
                      <div style={styles.propertyDetails}>
                        {property.bedrooms} bed • {property.bathrooms} bath
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {fetchedDislikes.length > 6 && (
                <div style={styles.moreProperties}>
                  + {fetchedDislikes.length - 6} more properties
                </div>
              )}
            </div>
          )}

          {/* Lauft Assistant Conversation (read-only) */}
          <div style={styles.section} data-section="bot-transcript">
            <h3 style={styles.sectionTitle}>Lauft Assistant Conversation</h3>
            {!showBotTranscript ? (
              <button
                onClick={() => fetchBotConversation(user._id)}
                style={styles.viewBotButton}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(37, 99, 235, 0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                View assistant chat history
              </button>
            ) : botLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                Loading conversation…
              </div>
            ) : botMessages.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                This user hasn't chatted with the assistant yet.
              </div>
            ) : (
              <div style={styles.botTranscript}>
                {botMessages.map((msg, idx) => {
                  const isBot = msg.sender == null; // bot messages are stored with sender: null
                  return (
                    <div
                      key={msg._id || idx}
                      style={{ ...styles.botRow, justifyContent: isBot ? 'flex-start' : 'flex-end' }}
                    >
                      <div style={isBot ? styles.botBubble : styles.botUserBubble}>
                        <div style={{ ...styles.botBubbleText, ...(isBot ? {} : styles.botUserText) }}>{msg.content}</div>
                        <div style={{ ...styles.botBubbleTime, ...(isBot ? {} : { color: '#fff' }) }}>
                          {msg.createdAt
                            ? new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Empty / loading state for activity */}
          {!activityLoading && fetchedLikes.length === 0 && fetchedDislikes.length === 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Activity</h3>
              <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6, fontSize: '13px' }}>
                No swipe activity yet.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Property Details Modal */}
      {showPropertyModal && selectedProperty && (
        <PropertyDetailsModal
          property={selectedProperty}
          isOpen={showPropertyModal}
          onClose={() => {
            setShowPropertyModal(false);
            setSelectedProperty(null);
          }}
        />
      )}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(17, 17, 17, 0.5)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    maxWidth: '720px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid #f0f0f0',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '22px 24px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    margin: 0,
    color: '#111',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorAlert: {
    backgroundColor: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    color: '#991b1b',
    padding: '11px 14px',
    margin: '16px 24px 0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 500,
  },
  successAlert: {
    backgroundColor: 'rgba(34, 197, 94, 0.08)',
    border: '1px solid rgba(34, 197, 94, 0.25)',
    color: '#166534',
    padding: '11px 14px',
    margin: '16px 24px 0',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 500,
  },
  content: {
    padding: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '14px',
  },
  sectionIcon: {
    color: '#2563eb',
  },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 600,
    margin: 0,
    color: '#9ca3af',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  poolAssignment: {
    backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: '12px',
    padding: '16px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    color: '#9ca3af',
    fontSize: '11px',
    fontWeight: 600,
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  select: {
    width: '100%',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '9px',
    padding: '9px 12px',
    color: '#111',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
  },
  poolActions: {
    display: 'flex',
    gap: '10px',
    marginBottom: '8px',
  },
  assignButton: {
    flex: 1,
    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '9px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.35)',
    fontFamily: 'inherit',
  },
  unassignButton: {
    flex: 1,
    backgroundColor: '#ffffff',
    color: '#dc2626',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    padding: '10px 16px',
    borderRadius: '9px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  currentPool: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: 'rgba(37, 99, 235, 0.08)',
    border: '1px solid rgba(37, 99, 235, 0.2)',
    borderRadius: '9px',
  },
  currentPoolLabel: {
    color: '#6b7280',
    fontSize: '12px',
  },
  currentPoolValue: {
    color: '#1d4ed8',
    fontSize: '12px',
    fontWeight: 700,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '14px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  infoIcon: {
    color: '#9ca3af',
    marginTop: '2px',
    flexShrink: 0,
  },
  infoLabel: {
    color: '#9ca3af',
    fontSize: '10px',
    margin: '0 0 3px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 600,
  },
  infoValue: {
    color: '#111',
    fontSize: '13px',
    margin: 0,
    fontWeight: 500,
  },
  leadInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
  leadBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(37, 99, 235, 0.08)',
    border: '1px solid rgba(37, 99, 235, 0.2)',
    padding: '6px 12px',
    borderRadius: '999px',
  },
  leadTierEmoji: {
    fontSize: '1rem',
  },
  leadTierText: {
    color: '#1d4ed8',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  leadStatusBadge: {
    backgroundColor: '#f3f4f6',
    padding: '6px 12px',
    borderRadius: '999px',
    color: '#6b7280',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  lauftqBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(253, 215, 0, 0.12)',
    border: '1px solid rgba(253, 215, 0, 0.45)',
    padding: '6px 12px',
    borderRadius: '999px',
  },
  lauftqCode: {
    color: '#7a6a00',
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.08em',
  },
  lauftqName: {
    color: '#6b7280',
    fontSize: '11px',
    fontWeight: 600,
  },
  assignedAgentInfo: {
    color: '#6b7280',
    fontSize: '12px',
  },
  onboardingGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  onboardingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  onboardingLabel: {
    color: '#6b7280',
    fontSize: '13px',
  },
  onboardingValue: {
    color: '#111',
    fontSize: '13px',
    fontWeight: 500,
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: '12px',
    padding: '20px',
  },
  statBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
  },
  statBoxClickable: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
    cursor: 'pointer',
    borderRadius: '10px',
    padding: '6px 10px',
    transition: 'background 0.15s',
  },
  statNumber: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#111',
    lineHeight: 1,
  },
  statLabel: {
    color: '#9ca3af',
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  statDivider: {
    width: '1px',
    height: '40px',
    backgroundColor: '#e5e7eb',
  },
  propertyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '14px',
  },
  propertyCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid #f0f0f0',
    transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
    cursor: 'pointer',
  },
  propertyImage: {
    width: '100%',
    height: '140px',
    objectFit: 'cover',
    display: 'block',
  },
  propertyInfo: {
    padding: '10px 12px',
  },
  propertyAddress: {
    color: '#111',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  propertyCsz: {
    color: '#6b7280',
    fontSize: '11px',
    marginBottom: '4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  propertyPrice: {
    color: '#1d4ed8',
    fontSize: '13px',
    fontWeight: 700,
  },
  propertyDetails: {
    color: '#6b7280',
    fontSize: '11px',
    marginTop: '4px',
  },
  moreProperties: {
    marginTop: '12px',
    color: '#9ca3af',
    fontSize: '12px',
    textAlign: 'center',
    fontWeight: 500,
  },
  viewBotButton: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1d4ed8',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  botTranscript: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '360px',
    overflowY: 'auto',
    padding: '4px',
    backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
    borderRadius: '12px',
  },
  botRow: {
    display: 'flex',
    width: '100%',
  },
  botBubble: {
    maxWidth: '78%',
    backgroundColor: '#f5f4f0',
    border: '1px solid #ececec',
    borderRadius: '14px 14px 14px 4px',
    padding: '8px 12px',
  },
  botUserBubble: {
    maxWidth: '78%',
    backgroundColor: '#7a9471',
    borderRadius: '14px 14px 4px 14px',
    padding: '8px 12px',
  },
  botBubbleText: {
    fontSize: '13px',
    lineHeight: 1.45,
    color: '#1a1a1a',
    whiteSpace: 'pre-wrap',
    WebkitTextFillColor: 'currentColor',
  },
  botUserText: {
    color: '#ffffff',
    WebkitTextFillColor: '#ffffff',
  },
  botBubbleTime: {
    fontSize: '10px',
    opacity: 0.55,
    marginTop: '4px',
  },
};

export default AdminUserModal;